import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchCached, resolveImageUrl } from '@/lib/api';
import BrandLogo from '@/components/BrandLogo';
import { prefetchPublicRoute } from '@/lib/publicPageData';

const LOGO_FALLBACK = '/logo.png';

export default function Footer() {
  const [settings, setSettings] = useState(null);
  const [footerLink, setFooterLink] = useState({ creator_name: 'IG DESIGN LAB', creator_url: 'https://wa.me/50937007500' });
  const [donationsEnabled, setDonationsEnabled] = useState(false);

  useEffect(() => {
    fetchCached('/content/site_settings').then(setSettings).catch(() => {});
    fetchCached('/content/footer_link').then(setFooterLink).catch(() => {});
    fetchCached('/donations/settings').then(data => setDonationsEnabled(data.has_active_methods === true)).catch(() => {});
  }, []);

  const logoUrl = settings?.logo_url ? resolveImageUrl(settings.logo_url) : LOGO_FALLBACK;

  return (
    <footer data-testid="site-footer" className="mscm-futuristic-band text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-16">
        <div className="relative z-10 grid min-w-0 grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand */}
          <div className="min-w-0">
            <BrandLogo
              src={logoUrl}
              alt="Million Stars Community Ministry"
              variant="footer"
              frameClassName="mb-4"
            />
            <p className="text-white/70 text-sm leading-relaxed max-w-xs">
              Shining the light of Christ among the nations, uniting people of all backgrounds into God's family.
            </p>
          </div>

          {/* Links */}
          <div className="min-w-0">
            <h4 className="text-white font-semibold mb-4 text-base" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Quick Links</h4>
            <nav className="flex flex-col gap-2">
              {[
                { to: '/about', label: 'About Us' },
                { to: '/statement-of-faith', label: 'Statement of Faith' },
                { to: '/founder', label: 'Our Founder' },
                { to: '/activities', label: 'Activities' },
                { to: '/gallery', label: 'Gallery' },
                { to: '/blog', label: 'Blog' },
                { to: '/contact', label: 'Contact' },
                ...(donationsEnabled ? [{ to: '/donate', label: 'Donate' }] : []),
              ].map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  onMouseEnter={() => prefetchPublicRoute(link.to)}
                  onFocus={() => prefetchPublicRoute(link.to)}
                  data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="rounded-sm text-sm text-white/70 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div className="min-w-0">
            <h4 className="text-white font-semibold mb-4 text-base" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Get in Touch</h4>
            <div className="flex min-w-0 flex-col gap-2 text-white/70 text-sm">
              <Link to="/contact" data-testid="footer-contact-link" className="rounded-sm transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40">
                Contact Us
              </Link>
              <a href="mailto:info@millionstarsministry.org" className="rounded-sm break-all transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40">
                info@millionstarsministry.org
              </a>
            </div>
          </div>
        </div>

        <div className="relative z-10 mt-12 pt-8 border-t border-white/10 text-center">
          <p data-testid="footer-copyright" className="text-white/60 text-sm">
            &copy; Million Stars Community Ministry - Created by{' '}
            <a
              href={footerLink.creator_url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-iglab-link"
              className="rounded-sm text-[#B9DCFF] hover:text-white font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              {footerLink.creator_name || 'IG DESIGN LAB'}
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
